import React from 'react';

import { Alert, styled, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import HighlightOffer from 'components/Highlight/assets/HighlightOffer';
import HighlightBanner from 'components/Highlight/highlight-banner';
import AppleLogo from 'components/SvgComponents/icons/Apple';
import GoogleLogo from 'components/SvgComponents/icons/Google';
import MicrosoftLogo from 'components/SvgComponents/icons/Microsoft';
import LoginEmailForm from 'containers/LoginPage/components/LoginEmailForm';
import LoginIdpButton from 'containers/LoginPage/components/LoginIdpButton';
import LoginPageSubtitle from 'containers/LoginPage/components/LoginPageSubtitle';
import useAuthSession from 'hooks/useAuthSession';
import useGlobalErrorHandler from 'hooks/useGlobalErrorHandler';
import { useLazyFetchUserAuthenticationInfoQuery } from 'redux/api/user.api';
import globalMessages from 'translations/messages/global-messages';
import { IDP } from 'utils/constants';

const Div_Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  '& .login-welcome-idp-wrapper': {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(4),
    margin: theme.spacing(8, 0),
    '& button': {
      width: 280,
    },
  },
  '& .login-welcome-invitation-alert': {
    width: '70%',
    fontSize: theme.typography.body.fontSize,
    marginBottom: theme.spacing(4),
  },
}));

const LoginWelcome = ({ invitationEmail, onAuthenticate }) => {
  // #region [intl]
  const intl = useIntl();
  // #endregion

  // #region [router]
  const location = useLocation();
  // #endregion

  // #region [auth]
  const { loginWithRedirect } = useAuthSession();
  // #endregion

  // #region [redux]
  const dispatchGlobalError = useGlobalErrorHandler();
  const [
    fetchUserInfo,
    { isLoading: fetchingUserInfo },
  ] = useLazyFetchUserAuthenticationInfoQuery();
  // #endregion

  // #region [methods]
  const handleIDPButtonClick = async connectionName => {
    try {
      await loginWithRedirect({
        authorizationParams: {
          connection: connectionName,
        },
      });
    } catch (err) {
      dispatchGlobalError({ error: err });
    }
  };

  const handleEmailSubmit = async email => {
    try {
      const orgIdMatch = location.search.match(/organizations\/([^/]+)/);
      const organizationId = orgIdMatch ? orgIdMatch[1] : null;
      const { search } = location;
      const deepLinkInfoId = search.split('deep_link_info_id%3D')[1]?.split('&')[0];
      const userInfo = await fetchUserInfo({
        email,
        organizationId,
        deepLinkInfoId,
      }).unwrap();
      if (!userInfo.is_email_verified) return;
      if (userInfo.connection_name || userInfo.idp_info) {
        // If the user has a custom identity provider, we redirect them to the IDP login page
        await loginWithRedirect({
          authorizationParams: {
            connection: userInfo.connection_name,
            login_hint: email,
          },
        });
        return;
      }
      onAuthenticate({ ...userInfo, email });
    } catch (err) {
      dispatchGlobalError({ error: err });
    }
  };
  // #endregion

  // #region [render]
  return (
    <Div_Wrapper>
      <Typography variant="header" component="p">
        {intl.formatMessage(globalMessages.welcome_to_finalcad)}
      </Typography>
      <LoginPageSubtitle>{intl.formatMessage(globalMessages.sign_in_or_sign_up)}</LoginPageSubtitle>
      <div className="login-welcome-idp-wrapper">
        <LoginIdpButton
          icon={<GoogleLogo viewBox="0 -3 24 24" />}
          label={intl.formatMessage(globalMessages.continue_google)}
          onClick={() => handleIDPButtonClick(IDP.google)}
        />
        <LoginIdpButton
          icon={<MicrosoftLogo viewBox="0 -3 24 24" />}
          label={intl.formatMessage(globalMessages.continue_microsoft)}
          onClick={() => handleIDPButtonClick(IDP.windows)}
        />
        <LoginIdpButton
          icon={<AppleLogo />}
          label={intl.formatMessage(globalMessages.continue_apple)}
          onClick={() => handleIDPButtonClick(IDP.apple)}
        />
      </div>
      <Typography variant="bodyLight" component="p">
        {intl.formatMessage(globalMessages.or)}
      </Typography>
      <LoginEmailForm loading={fetchingUserInfo} onSubmit={handleEmailSubmit} />
      {invitationEmail && (
        <Alert className="login-welcome-invitation-alert" variant="filled" severity="info">
          {intl.formatMessage(globalMessages.invitation_deeplink_info, { email: invitationEmail })}
        </Alert>
      )}
      <HighlightBanner
        onTitle={intl.formatMessage(globalMessages.banner_highlight_ontitle)}
        title={intl.formatMessage(globalMessages.banner_highlight_title)}
        image={<HighlightOffer />}
      />
    </Div_Wrapper>
  );
  // #endregion
};

LoginWelcome.propTypes = {
  invitationEmail: PropTypes.string,
  onAuthenticate: PropTypes.func.isRequired,
};

export default LoginWelcome;
